import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';

import { pageMetadata } from '@flakiness/server/common/pageMetadata.js';
import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { CHECK } from '@flakiness/shared/common/utils.js';
import { ContextType } from '@lit/context';
import { Task } from '@lit/task';
import { SlCheckbox, SlDialog, SlInput, SlRadioGroup, SlSelect } from '@shoelace-style/shoelace';
import { live } from 'lit/directives/live.js';
import { api } from './api.js';
import { docLinkStyles, linkStyles, pageStyles } from './components/cssstyles.js';
import { PickProjectName, ProjectSlugPicked } from './components/pick-project-name.js';
import { ProjectVisibilityEvent } from './components/pick-project-visibility.js';
import { ProjectContext } from './components/project-context.js';
import { HISTORY_TYPE_LABELS, OFFERED_HISTORY_TYPES } from './components/report-history-selector.js';
import { consume, contexts, FKProjectChanged, TaskEvent } from './contexts.js';
import { RELINKABLE_KINDS, SourceCodeIntegration, SourceCodeIntegrationEvent } from './features/feature-source-code-integration.js';
import { forgeUtils } from './forgeUtils.js';
import { renderRelativeDate } from './fragments.js';
import { githubMarkdownCSS } from './githubMarkdownCSS.js';
import { PageOrganization } from './page-organization.js';
import { PageProjectTests } from './page-project-tests.js';
import { docsUrl, platformUrl, projectPlatformUrl } from './platformUrl.js';
import { RouteConfig } from './router.js';
import { tasks } from './tasks.js';

const FLAKINESS_PERCENTAGE_PRESETS = [0, 1, 1.5, 3, 5];

@customElement('page-project-settings')
export class PageProjectSettings extends LitElement {

  static url(options: { orgSlug: string, projectSlug: string, tab?: 'general'|'usage', snapshotId: WireTypes.DataSnapshotId | undefined }): string {
    return projectPlatformUrl(['', options.orgSlug, options.projectSlug, 'settings'].join('/'), undefined, options.snapshotId);
  }

  static routes(): RouteConfig[] {
    return [{
      path: [
        '/:org/:project/settings',
      ],
      render: (groups) => ProjectContext.render({
        orgSlug: groups?.org,
        projectSlug: groups?.project,
      }, html`
        <page-project-settings
          .orgSlug=${groups?.org}
          .projectSlug=${groups?.project}
        ></page-project-settings>
      `),
    }];
  }

  @consume({ context: contexts.user }) private _user?: ContextType<typeof contexts.user>;
  @consume({ context: contexts.router }) private _router?: ContextType<typeof contexts.router>;
  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;
  @consume({ context: contexts.dataSnapshotId, subscribe: true }) private _snapshotId?: ContextType<typeof contexts.dataSnapshotId>;
  @consume({ context: contexts.billing, subscribe: true }) private _billing?: ContextType<typeof contexts.billing>;
  @consume({ context: contexts.serverInfo, subscribe: true }) private _serverInfo?: ContextType<typeof contexts.serverInfo>;

  @property({ attribute: false }) projectSlug?: string;
  @property({ attribute: false }) orgSlug?: string;
  
  @query('#link-dialog') private _linkDialog?: SlDialog;
  @query('pick-project-name') private _projectSlugInput?: PickProjectName;

  @state() private _integration?: SourceCodeIntegration;
  @state() private _projectRename?: ProjectSlugPicked;
  @state() private _slackWebhookUrl = '';
  @state() private _slackTestStatus?: 'loading' | 'success' | 'error';

  private _fetchedRepositoryStats = new Task(this, {
    args: () => [this.orgSlug, this.projectSlug, this._snapshotId] as const,
    task: async ([orgSlug, projectSlug, snapshotId], { signal }) => {
      CHECK(orgSlug && projectSlug && snapshotId);
      return await api.project.fetchRepositoryStats.GET({ orgSlug, projectSlug, snapshotId }, { signal });
    },
  });

  private _badgeTask = new Task(this, {
    args: () => [this.orgSlug, this.projectSlug] as const,
    task: async ([orgSlug, projectSlug], { signal }) => {
      CHECK(orgSlug && projectSlug);
      const result = await api.project.getBadge.GET({ orgSlug, projectSlug }, { signal });
      return result?.badgeToken;
    },
  });

  private _slackWebhookConfigured = new Task(this, {
    args: () => [this.orgSlug, this.projectSlug] as const,
    task: async ([orgSlug, projectSlug], { signal }) => {
      CHECK(orgSlug && projectSlug);
      return await api.project.isSlackWebhookConfigured.GET({ orgSlug, projectSlug }, { signal });
    },
  });

  private _linkSourceTask = new Task(this, {
    autoRun: false,
    args: () => [this.orgSlug, this.projectSlug, this._integration] as const,
    task: async ([orgSlug, projectSlug, integration], { signal }) => {
      CHECK(orgSlug && projectSlug && integration);
      CHECK(integration.kind !== 'local_checkout' && integration.kind !== 'json_repo');
      await api.project.setProjectSource.POST({
        orgSlug, projectSlug,
        source: integration,
      }, { signal });
      this.dispatchEvent(new FKProjectChanged({ orgSlug, projectSlug }));
      this._integration = undefined;
      this._linkDialog?.hide();
    },
  });

  private async _renameProject() {
    const newProjectSlug = this._projectSlugInput?.value();
    const orgSlug = this.orgSlug;
    const projectSlug = this.projectSlug;
    if (!projectSlug || !newProjectSlug || !orgSlug)
      return;
    const success = await api.project.setProjectSlug.POST({
      orgSlug,
      projectSlug,
      newProjectSlug,
    }).then(() => true).catch(e => {
      console.error(e);
      return false;
    })
    this._projectSlugInput?.setStatus(success, success ? 'Project name updated' : 'Failed to update project name');
    if (success) {
      this._projectRename = undefined;
      await this._router?.navigate(PageProjectSettings.url({
        orgSlug: orgSlug,
        projectSlug: newProjectSlug,
        snapshotId: this._snapshotId,
      }));
    }
  }

  private async _rotateAccessToken() {
    const project = this._project;
    if (!project)
      return;
    if (!confirm('Rotating the access token will invalidate the current token. Any CI pipelines or CLI configurations using the old token will stop working. Continue?'))
      return;
    await api.project.rotateAccessToken.POST({
      orgSlug: project.org.orgSlug,
      projectSlug: project.projectSlug,
    });
    this.dispatchEvent(new FKProjectChanged({
      orgSlug: project.org.orgSlug,
      projectSlug: project.projectSlug,
    }));
  }

  private _canRenameProject(): boolean {
    const currentProjectSlug = this.projectSlug;
    const newProjectSlug = this._projectSlugInput?.value();
    return !!currentProjectSlug && !!newProjectSlug && newProjectSlug !== currentProjectSlug && !!this._projectSlugInput?.success();
  }

  private async _onVisibilityChanged(event: ProjectVisibilityEvent) {
    const project = this._project;
    if (!project)
      return;
    await api.project.setVisibility.POST({
      orgSlug: project.org.orgSlug,
      projectSlug: project.projectSlug,
      visibility: event.visibility,
    });
    this.dispatchEvent(new FKProjectChanged({
      orgSlug: project.org.orgSlug,
      projectSlug: project.projectSlug,
    }));
  }

  private _pageMetadataTask = tasks.pageMetadata(this, {
    args: () => [this.orgSlug, this.projectSlug] as const,
    metadata: ([orgSlug, projectSlug]) => orgSlug && projectSlug ? pageMetadata({
      orgSlug, projectSlug, section: 'Settings', accessGated: true,
    }) : undefined,
  });

  render() {
    if (!this._project)
      return nothing;

    return html`
      <sl-dialog id=link-dialog style="--width: 75ch;" label="Change repository or update access">
        <div>
          Use this dialog to switch repositories, move the project to another
          provider, or refresh access after credentials expire.
          Changing the linked repository is a <b>non-destructive</b> operation.
        </div>
        <feature-source-code-integration
          .allowedKinds=${RELINKABLE_KINDS}
          @source-code-integration=${(event: SourceCodeIntegrationEvent) => this._integration = event.detail}
        ></feature-source-code-integration>
        <sl-button ?disabled=${!this._integration} slot="footer" variant="primary" @click=${() => this._linkSourceTask.run()}>Save repository access</sl-button>
      </sl-dialog>

      <project-header
        .orgSlug=${this.orgSlug}
        .projectSlug=${this.projectSlug}
        .submenu=${"settings"}
      ></project-header>
      <app-body user-role=${this._project.access}>
        <form-section is-editor label="Flakiness Access Token">
          <h-box style="align-items: center; gap: var(--sl-spacing-2x-small);">
            <sl-copy-button id=copy-button value=${this._project.readWriteAccessToken}></sl-copy-button>
            <sl-tag size=medium>${this._project.readWriteAccessToken}</sl-tag>
            <sl-button size=small variant=danger @click=${() => this._rotateAccessToken()}>Rotate</sl-button>
          </h-box>
        </form-section>

        <form-section label="Project name">
          <pick-project-name
            ?disabled=${this._project.access !== 'editor'}
            .orgSlug=${this._project.org.orgSlug}
            .initial=${live(this._project.projectSlug)}
            .existing=${this._project.projectSlug}
            @project-slug=${(event: TaskEvent<ProjectSlugPicked>) => this._projectRename = event.data }
          >
            <sl-button ?disabled=${!this._canRenameProject()} @click=${() => this._renameProject()}>Update Name</sl-button>
          </pick-project-name>
        </form-section>

        <form-section label="Acceptable Flaky Test Ratio">
          ${this._renderFlakinessRatio(this._project)}
        </form-section>

        <form-section label="Project Visibility" style="margin-top: -var(--sl-spacing-medium);">
          <pick-project-visibility
            ?disabled=${this._project.access !== 'editor'}
            .initial=${this._project?.visibility}
            .allowedVisibilities=${this._project?.org.allowedVisibilities}
            @project-visibility=${this._onVisibilityChanged}
          ></pick-project-visibility>
        </form-section>

        <form-section label="Project Timezone" style="margin-top: -var(--sl-spacing-medium);">
          <timezone-selector
            ?disabled=${this._project.access !== 'editor'}
          ></timezone-selector>
        </form-section>

        <form-section label="Default History" style="margin-top: -var(--sl-spacing-medium);">
          <p>How the report's history column is grouped by default. Dashboard users can override this locally.</p>
          <sl-radio-group
            value=${this._project.defaultHistoryType ?? 'commit'}
            @sl-change=${(event: Event) => this._setDefaultHistoryType((event.target as SlRadioGroup).value)}
          >
            ${OFFERED_HISTORY_TYPES.map(historyType => html`
              <sl-radio-button ?disabled=${this._project?.access !== 'editor'} value=${historyType}>${HISTORY_TYPE_LABELS[historyType]}</sl-radio-button>
            `)}
          </sl-radio-group>
        </form-section>

        ${forgeUtils.isGithub(this._project) ? html`
          <form-section label="GitHub Reporting" style="margin-top: -var(--sl-spacing-medium);">
            <p>Report test results back to GitHub.</p>
            <v-box style="gap: var(--sl-spacing-2x-small);">
              <sl-checkbox
                ?disabled=${this._project.access !== 'editor'}
                ?checked=${this._project.githubChecksBranchCommits}
                @sl-change=${(event: Event) => this._setGithubReporting({ branchCommitChecks: (event.target as SlCheckbox).checked })}
              >Status checks for commits</sl-checkbox>
              <sl-checkbox
                ?disabled=${this._project.access !== 'editor'}
                ?checked=${this._project.githubChecksPullRequests}
                @sl-change=${(event: Event) => this._setGithubReporting({ pullRequestChecks: (event.target as SlCheckbox).checked })}
              >Status checks for pull requests</sl-checkbox>
              <sl-checkbox
                ?disabled=${this._project.access !== 'editor'}
                ?checked=${this._project.githubPullRequestComments}
                @sl-change=${(event: Event) => this._setGithubReporting({ pullRequestComments: (event.target as SlCheckbox).checked })}
              >Pull request sticky comments</sl-checkbox>
            </v-box>
          </form-section>
        ` : nothing}

        <form-section label="Git Repository">
          <p>
            <h-box>
              <a-ext href=${forgeUtils.hasSourceUrl(this._project) ? forgeUtils.projectUrl(this._project) : nothing}>
                <h-box style="gap: var(--sl-spacing-2x-small);">
                  <sl-icon name=${forgeUtils.providerIcon(this._project)}></sl-icon>
                  <span>${this._project.sourceOwner}/${this._project.sourceRepo}</span>
                </h-box>
              </a-ext>
              <sl-button size=small is-editor @click=${() => this._linkDialog?.show()}>Change repository / update access</sl-button>
            </h-box>
          </p>
          <p>
            ${this._renderLastFetchAttempt(this._project)}
          </p>
        </form-section>

        <form-section is-editor label="Project Badge">
          ${this._renderBadgeSection(this._project)}
        </form-section>

        <form-section is-editor label="Slack Notifications">
          ${this._renderSlackNotifications(this._project)}
        </form-section>

        <sl-divider is-editor></sl-divider>
        <form-section is-editor label="Danger Zone">
          <sl-button @click=${this._deleteProject.bind(this)} variant=danger>Delete Project</sl-button>
        </form-section>

      </app-body>
      <app-footer></app-footer>
    `
  }

  private _renderFlakinessRatio(project: WireTypes.Project) {
    // Percentages have at most 2 decimals, e.g. 1.25%.
    const percentage = Math.round(project.acceptableFlakinessRatio * 10_000) / 100;
    const percentages = [...new Set([...FLAKINESS_PERCENTAGE_PRESETS, percentage])].sort((a, b) => a - b);
    return html`
      <sl-select
        style="width: 10rem;"
        hoist
        .value=${live(String(percentage))}
        ?disabled=${project.access !== 'editor'}
        @sl-change=${(event: Event) => this._setFlakinessPercentage(event.target as SlSelect, percentage)}
      >
        ${percentages.map(value => html`<sl-option value=${value}>${value}%</sl-option>`)}
        <sl-option value=custom>Custom…</sl-option>
      </sl-select>
    `;
  }

  private async _setFlakinessPercentage(select: SlSelect, currentPercentage: number) {
    const project = this._project;
    if (!project)
      return;
    let value = select.value as string;
    if (value === 'custom') {
      // Let the dropdown finish closing before the native prompt blocks the page.
      if (select.popup.active)
        await new Promise(resolve => select.addEventListener('sl-after-hide', resolve, { once: true }));
      value = prompt('Acceptable flaky test percentage (0–100):', String(currentPercentage)) ?? '';
    }
    const percentage = Math.round(Number(value) * 100) / 100;
    if (!value.trim() || Number.isNaN(percentage) || percentage < 0 || percentage > 100 || percentage === currentPercentage) {
      select.value = String(currentPercentage);
      return;
    }
    await api.project.setProjectAcceptableFlakinessRatio.POST({
      orgSlug: project.org.orgSlug,
      projectSlug: project.projectSlug,
      acceptableFlakinessRatio: percentage / 100,
    });
    this.dispatchEvent(new FKProjectChanged({
      orgSlug: project.org.orgSlug,
      projectSlug: project.projectSlug,
    }));
  }

  @query('#slack-webhook-input') private _slackWebhookInput?: SlInput;

  private async _setDefaultHistoryType(value: string) {
    const project = this._project;
    const historyType = WireTypes.HISTORY_TYPES.find(historyType => historyType === value);
    if (!project || !historyType || historyType === (project.defaultHistoryType ?? 'commit'))
      return;
    await api.project.setProjectDefaultHistoryType.POST({
      orgSlug: project.org.orgSlug,
      projectSlug: project.projectSlug,
      defaultHistoryType: historyType,
    });
    this.dispatchEvent(new FKProjectChanged({
      orgSlug: project.org.orgSlug,
      projectSlug: project.projectSlug,
    }));
  }

  private async _setGithubReporting(options: { branchCommitChecks?: boolean, pullRequestChecks?: boolean, pullRequestComments?: boolean }) {
    const project = this._project;
    if (!project)
      return;
    await api.project.setProjectGithubReporting.POST({
      orgSlug: project.org.orgSlug,
      projectSlug: project.projectSlug,
      ...options,
    });
    this.dispatchEvent(new FKProjectChanged({
      orgSlug: project.org.orgSlug,
      projectSlug: project.projectSlug,
    }));
  }

  private _renderBadgeSection(project: WireTypes.Project) {
    const { orgSlug, projectSlug } = { orgSlug: project.org.orgSlug, projectSlug: project.projectSlug };
    const description = html`
      <p>
        Embed a status badge in your README or documentation to show the current test status.
        Anyone with the link can see the badge, but only authenticated users can navigate to the project.
      </p>
    `;
    const badgeToken = this._badgeTask.value;
    if (!badgeToken) {
      return this._badgeTask.render({
        pending: () => description,
        complete: () => html`
          ${description}
          <sl-button
            variant=primary
            @click=${async () => {
              await api.project.generateBadge.POST({ orgSlug, projectSlug });
              this._badgeTask.run();
            }}
          >Generate Badge</sl-button>
        `
      });
    }

    const endpointUrl = api.badge.GET.prepare({ badgeToken }).url;
    const shieldsUrl = `https://img.shields.io/endpoint?url=${encodeURIComponent(endpointUrl.toString())}`;
    const projectUrl = PageProjectTests.url({ orgSlug, projectSlug, snapshotId: undefined });
    const markdown = `[![Tests](${shieldsUrl})](${projectUrl})`;
    return html`
      ${description}
      <v-box style="gap: var(--sl-spacing-small);">
        <img src=${shieldsUrl} alt="Tests badge" style="align-self: start;" />
        <v-box style="gap: var(--sl-spacing-2x-small);">
          <div style="font-size: var(--sl-font-size-small);">Markdown</div>
          <h-box style="gap: var(--sl-spacing-2x-small);">
            <sl-input style="flex: 1;" size=small readonly value=${markdown}></sl-input>
            <sl-copy-button value=${markdown}></sl-copy-button>
          </h-box>
        </v-box>
        <v-box style="gap: var(--sl-spacing-2x-small);">
          <div style="font-size: var(--sl-font-size-small);">Image URL</div>
          <h-box style="gap: var(--sl-spacing-2x-small);">
            <sl-input style="flex: 1;" size=small readonly value=${shieldsUrl}></sl-input>
            <sl-copy-button value=${shieldsUrl}></sl-copy-button>
          </h-box>
        </v-box>
        <h-box>
          <sl-button
            size=small
            @click=${async () => {
              await api.project.generateBadge.POST({ orgSlug, projectSlug });
              this._badgeTask.run();
            }}
          >Regenerate</sl-button>
          <sl-button
            size=small
            variant=danger
            @click=${async () => {
              await api.project.deleteBadge.POST({ orgSlug, projectSlug });
              this._badgeTask.run();
            }}
          >Delete</sl-button>
        </h-box>
      </v-box>
    `;
  }

  private _renderSlackNotifications(project: WireTypes.Project) {
    const { orgSlug, projectSlug } = { orgSlug: project.org.orgSlug, projectSlug: project.projectSlug };
    const description = html`
      <p>
      Receive alerts about test failures and regressions in Slack.
      </p>
      <p>
      Runs at 9 AM in the project's timezone and only sends a message if issues are detected.<br/>
      Both Incoming Webhooks and Workflow Triggers are supported.
      <a href="${docsUrl('/settings/slack-notifications/')}" target="_blank">Learn more</a>.
      </p>
    `;
    if (this._serverInfo?.enabledBilling && !this._billing?.subscription?.plan.premiumFeatures) {
      return html`
        ${description}
        <fk-callout variant=warn>
          Slack notifications are not available on your current plan. Upgrade your plan on the
          <a href=${platformUrl(['', orgSlug, 'billing'].join('/'))}>billing page</a> to enable them.
        </fk-callout>
      `;
    }
    if (!this._slackWebhookConfigured.value) {
      return html`
        ${description}
        <v-box style="gap: var(--sl-spacing-small);">
          <sl-input
            id=slack-webhook-input
            type=password
            password-toggle
            placeholder="https://hooks.slack.com/..."
            .value=${this._slackWebhookUrl}
            @sl-input=${(e: Event) => this._slackWebhookUrl = (e.target as HTMLInputElement).value}
            pattern="https:\/\/hooks\.slack\.com\/.+"
            required
            help-text="Must be a valid Slack webhook URL"
          ></sl-input>
          <h-box>
            <sl-button
              variant=primary
              ?disabled=${!this._slackWebhookUrl}
              @click=${async () => {
                if (!this._slackWebhookInput?.reportValidity())
                  return;
                await api.project.setProjectSlackWebhook.POST({ orgSlug, projectSlug, webhookUrl: this._slackWebhookUrl });
                this._slackWebhookUrl = '';
                this._slackWebhookConfigured.run();
              }}
            >Save Webhook</sl-button>
          </h-box>
        </v-box>
      `;
    }

    return html`
      ${description}
      <v-box style="gap: var(--sl-spacing-small);">
        <h-box>
          <sl-button
            size=small
            variant=${this._slackTestStatus === 'success' ? 'success' :
              this._slackTestStatus === 'error' ? 'warning' : 'default'
            }
            ?loading=${this._slackTestStatus === 'loading'}
            @click=${async () => {
              this._slackTestStatus = 'loading';
              try {
                await api.project.testProjectSlackWebhook.POST({ orgSlug, projectSlug });
                this._slackTestStatus = 'success';
              } catch {
                this._slackTestStatus = 'error';
              }
            }}
          >
            ${this._slackTestStatus === 'success' ? 'Test message sent' :
              this._slackTestStatus === 'error' ? 'Test message failed' :
              'Send test message'
            }
          </sl-button>
          <sl-button
            size=small
            variant=danger
            @click=${async () => {
              await api.project.setProjectSlackWebhook.POST({ orgSlug, projectSlug, webhookUrl: undefined });
              this._slackTestStatus = undefined;
              this._slackWebhookConfigured.run();
            }}
          >Remove</sl-button>
        </h-box>
      </v-box>
    `;
  }

  private _renderLastFetchAttempt(project: WireTypes.Project) {
    const { commits, branches, pullRequests } = this._fetchedRepositoryStats.value ?? {};
    if (project.sourceLastFetchHTTPCode === undefined || project.sourceLastFetchTime === undefined)
      return html`<span>fetching commits right now...</span>`;
    const relativeTime = html`
      <b>${renderRelativeDate(project.sourceLastFetchTime)}</b>
    `;

    const provider = forgeUtils.providerName(project);
    const info = commits === undefined || branches === undefined || pullRequests === undefined ? nothing :
      html`Fetched <b>${commits}</b> commits, <b>${branches}</b> branches and <b>${pullRequests}</b> ${forgeUtils.pullRequestLabelPlural(project).toLowerCase()}.`;
    const attempt = (project.sourceLastFetchHTTPCode === 200) ?
      html`<b>Successfully</b> pulled history ${relativeTime}` :
      forgeUtils.isAccessDenied(project) ?
      html`${provider} <b>denied access</b> when we tried to pull history ${relativeTime}` :
      forgeUtils.isUnavailable(project) ?
      html`${provider} was <sl-tooltip content="${provider} responded with HTTP ${project.sourceLastFetchHTTPCode}"><b>temporarily unavailable</b></sl-tooltip> when we tried to pull history ${relativeTime}` :
      html`<sl-tooltip content="${provider} responded with HTTP ${project.sourceLastFetchHTTPCode}"><b>Failed</b></sl-tooltip> to pull history ${relativeTime}`
    return html`
      <v-box style="gap: var(--sl-spacing-2x-small);">
        <div>${info}</div>
        <div>${attempt}</div>
      </v-box>
    `
  }

  async _deleteProject() {
    const { orgSlug, projectSlug } = this;
    if (!orgSlug || !projectSlug || !this._router)
      return;
    const value = prompt(`Type in "${orgSlug}/${projectSlug}" to confirm deletion`)
    if (value?.trim() !== `${orgSlug}/${projectSlug}`)
      return;
    await api.project.delete.POST({ orgSlug, projectSlug });
    this._router.navigate(PageOrganization.url({ orgSlug } ));
  }

  static styles = [linkStyles, githubMarkdownCSS, docLinkStyles, pageStyles, css`
    a-ext {
      color: rgb(9, 105, 218);
    }
    [user-role=viewer] {
      *:is([is-editor]) {
        display: none;
      }
    }

    feature-source-code-integration {
      margin: var(--sl-spacing-medium) 0;
    }

  `];
}
